import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MessageIcon from '@mui/icons-material/Message';
import SaveIcon from '@mui/icons-material/Save';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Card, Typography } from '@mui/material';
import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../../../context/AuthContext';
import { decrypt } from '../../../../utils/cryptojs-helper';
import BaseInput from '../../../common/BaseInput';
import Loader from '../../../common/loader';
import { API_LANE_MANAGEMENT } from '../../api';
import { useLaneContext } from '../../context/LaneContext';
import s from './styles.module.scss';
import createImageFromHtml from './utils/create-image-from-html';
import { FileUpload, FileUploadSelectParams } from 'primereact/fileupload';
import toast from 'react-hot-toast';
import './FileUpload.css';

const CustomMessage = () => {
  const uploadFileRef = useRef<FileUpload | null>(null);
  const { screen, view } = useLaneContext();
  const { userNew } = useAuthContext();
  const decrypted = decrypt(userNew);

  const user = JSON.parse(decrypted);
  const currentClient = user.currentClient.client.name;

  const exportRef = useRef() as MutableRefObject<HTMLInputElement>;
  const navigate = useNavigate();

  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);

  const createCustomImage = useCallback(
    async (element: HTMLInputElement) => {
      const string = await createImageFromHtml(element);
      const success = await API_LANE_MANAGEMENT.createCustom(setLoading, string, currentClient, screen);

      // if success refresh page else do nothing
      success
        ? setTimeout(() => {
            navigate(0);
          }, 2000)
        : '';
    },
    [navigate, currentClient, screen]
  );

  const checkImageDimensions = (file: File): Promise<void> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        if (img.width === 4800 && img.height === 360) {
          resolve();
        } else {
          if (uploadFileRef && uploadFileRef.current) {
            uploadFileRef.current.clear();
          }
          reject(new Error(`Invalid image dimensions: ${img.width}x${img.height}. Expected: 4800x360.`));
        }
      };
      img.onerror = () => reject(new Error('Could not load image.'));
      img.src = URL.createObjectURL(file);
    });
  };

  const onSelectImg = async (e: FileUploadSelectParams) => {
    const file = e.files[0];
    try {
      await checkImageDimensions(file);
      // Valid dimensions, you can proceed with uploading
    } catch (err) {
      toast.error('Image does not meet the requirements');
    }
  };

  const handleUploadError = () => {
    toast.error('Failed to upload the media. Please try again.');
    return;
  };

  const handleSuccessfulUpload = () => {
    toast.success('Successfully uploaded new Media!');
    // if success refresh page else do nothing
    setTimeout(() => {
      navigate(0);
    }, 2000);
  };

  useEffect(() => {
    if (view == 'static' && uploadFileRef && uploadFileRef.current) {
      uploadFileRef.current.clear();
    }
    // eslint-disable-next-line
  }, [screen]);

  return view == 'dynamic' ? (
    <Accordion className={s.accordion}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls='panel1a-content'>
        <div className={s.title}>
          Custom Message <MessageIcon className={s.icon} />
        </div>
      </AccordionSummary>
      <AccordionDetails>
        <Card className={s.container}>
          {loading ? (
            <Loader />
          ) : (
            <>
              <Box className={s.layer}>
                <Box className={s.customImage} ref={exportRef}>
                  <h1>{text}</h1>
                </Box>
              </Box>
              <Box mt={1} width={550}>
                <BaseInput
                  placeholder='Enter Custom Text...'
                  size='medium'
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                />
              </Box>
              <Button
                variant='contained'
                size='large'
                sx={{ mt: 1 }}
                endIcon={<SaveIcon />}
                onClick={() => createCustomImage(exportRef.current)}
              >
                Save
              </Button>
            </>
          )}
        </Card>
      </AccordionDetails>
    </Accordion>
  ) : (
    <Box
      bgcolor={'#fff'}
      border={'1px solid #d2d8e4'}
      borderRadius={2}
      width={'100%'}
      display={'flex'}
      flexDirection={'column'}
      alignItems={'center'}
      justifyContent={'center'}
      mt={2}
      mb={2}
      maxWidth={1340}
    >
      <Typography width={'100%'} pl={2} fontWeight={'bold'} mb={1} borderBottom={'1px solid #d2d8e4'}>
        Image Uploader
      </Typography>
      <Typography width={'100%'} pl={2} fontWeight={'bold'} textAlign={'left'} fontSize={14}>
        Requirements for image:
      </Typography>
      <Typography width={'100%'} pl={2} textAlign={'left'} fontSize={12}>
        Dimensions: 4800x360px
      </Typography>
      <Typography width={'100%'} pl={2} textAlign={'left'} fontSize={12}>
        Less than 150KB
      </Typography>
      <FileUpload
        ref={uploadFileRef}
        className={s.fileUploadContainer}
        url={`${process.env.REACT_APP_API_BASE_URL}/image/static/select/${currentClient}/${screen}`}
        // eslint-disable-next-line
        withCredentials={true}
        name='media[]' // name 'media[]' is what is called in the api, also has to be array to handle multiple files
        accept='image/*'
        maxFileSize={150000}
        onSelect={onSelectImg}
        onError={handleUploadError}
        onUpload={handleSuccessfulUpload}
        emptyTemplate={<p>Drag and drop file here to upload.</p>}
      />
    </Box>
  );
};

export default CustomMessage;
